import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import {
  FieldErrors,
  FieldValues,
  FormProvider,
  Path,
  UseFormReturn,
} from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { flattenFieldErrors } from '@utils';
import { FormActionsFooter } from '../FormActionsFooter';
import { PageHeader } from '../PageHeader';
import { Stepper } from '../Stepper';

type FormPageProps<T extends FieldValues> = {
  header: string;
  children: React.ReactNode;
  steps?: string[];
  pages: { name: string; href: string; current: boolean }[];
  methods: UseFormReturn<T>;
  /** Omit both step props for a single-page form: no stepper, no Continue/Go Back. */
  currentStep?: number;
  setCurrentStep?: React.Dispatch<React.SetStateAction<number>>;
  onSubmit: (data: T) => void;
  onSaveDraft?: (data: T) => void;
  isSubmitting?: boolean;
  isSavingDraft?: boolean;
  customActions?: React.ReactNode;
  submitText?: string;
  /** Optional mapping of step number to field names for step-specific validation */
  stepFields?: Record<number, Path<T>[]>;
  /** URL to navigate to when Cancel is clicked. Falls back to browser back. */
  cancelUrl?: string;
  /** Error message to display above the footer (e.g. after a failed submission) */
  submitError?: string | null;
  /** Callback to dismiss the submit error */
  onDismissError?: () => void;
  /** When false, the submit button is disabled (e.g. a backend verdict blocks submission) */
  canSubmit?: boolean;
};

function findNearestNamedElement(fieldPath: string) {
  const segments = fieldPath.split('.');

  while (segments.length > 0) {
    const element = document.querySelector(
      `[name="${segments.join('.')}"]`
    ) as HTMLElement | null;
    if (element) return element;
    segments.pop();
  }

  return null;
}

export function FormPage<T extends FieldValues>({
  header,
  children,
  steps,
  pages,
  methods,
  currentStep = 1,
  setCurrentStep = () => {},
  onSubmit,
  onSaveDraft,
  isSubmitting = false,
  isSavingDraft = false,
  customActions,
  submitText,
  stepFields,
  cancelUrl,
  submitError,
  onDismissError,
  canSubmit = true,
}: FormPageProps<T>) {
  const navigate = useNavigate();
  const { trigger, handleSubmit, getValues } = methods;

  const [validationSummary, setValidationSummary] = useState<string | null>(
    null
  );

  const handleSaveDraft = onSaveDraft
    ? () => {
        onSaveDraft(getValues());
      }
    : undefined;

  const handleFormSubmit = (data: T) => {
    setValidationSummary(null);
    onSubmit(data);
  };

  const handleInvalidSubmit = (errors: FieldErrors<T>) => {
    const invalidFields = flattenFieldErrors(errors);
    const [firstInvalidField] = invalidFields;

    if (firstInvalidField) {
      const [topLevelField] = firstInvalidField.path.split('.');

      if (stepFields) {
        const targetStep = Object.keys(stepFields).find((step) =>
          stepFields[Number(step)]?.includes(topLevelField as Path<T>)
        );
        if (targetStep && Number(targetStep) !== currentStep) {
          setCurrentStep(Number(targetStep));
        }
      }

      requestAnimationFrame(() => {
        const el = findNearestNamedElement(firstInvalidField.path);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el?.focus?.({ preventScroll: true });
      });
    }

    const messages = Array.from(
      new Set(invalidFields.map((invalidField) => invalidField.message))
    ).slice(0, 3);

    setValidationSummary(
      messages.length > 0
        ? messages.join(' ')
        : 'Please fix the highlighted fields before submitting.'
    );
  };

  const bannerMessage = submitError || validationSummary;

  const handleDismissBanner = () => {
    setValidationSummary(null);
    onDismissError?.();
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(handleFormSubmit, handleInvalidSubmit)}
        noValidate
        className='h-[calc(100vh-64px)] flex flex-col'
      >
        <PageHeader header={header} pages={pages}>
          {steps && steps.length > 0 && (
            <Stepper steps={steps} currentStep={currentStep} />
          )}
        </PageHeader>
        <div className='flex flex-col space-y-4 px-10 py-6 grow'>
          {children}
        </div>
        {bannerMessage && (
          <div className='flex items-center gap-3 px-10 py-3 bg-red-50 border-t border-red-200'>
            <ExclamationTriangleIcon className='h-5 w-5 text-red-500 shrink-0' />
            <p className='text-sm text-red-700 grow'>{bannerMessage}</p>
            <button
              type='button'
              onClick={handleDismissBanner}
              className='shrink-0 text-red-400 hover:text-red-600'
            >
              <XMarkIcon className='h-5 w-5' />
            </button>
          </div>
        )}
        <FormActionsFooter
          currentStep={currentStep}
          stepsLength={steps?.length || 1}
          onContinue={async () => {
            // If stepFields is provided for current step, validate only those fields.
            // Otherwise, just proceed (allows steps with no required fields).
            const fieldsToValidate = stepFields?.[currentStep];
            if (fieldsToValidate && fieldsToValidate.length > 0) {
              const isValid = await trigger(fieldsToValidate);
              if (isValid) {
                setCurrentStep((prev) => prev + 1);
                return;
              }
              // Validation failed — scroll to the first invalid field so the user
              // sees the blocker instead of the Continue button silently doing nothing.
              const errors = methods.formState.errors as Record<
                string,
                unknown
              >;
              const firstErrorField = fieldsToValidate.find(
                (f) => errors[f as string]
              );
              if (firstErrorField) {
                const el = document.querySelector(
                  `[name="${String(firstErrorField)}"]`
                ) as HTMLElement | null;
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el.focus?.({ preventScroll: true });
                }
                // eslint-disable-next-line no-console
                console.warn(
                  '[FormPage] Continue blocked — invalid field:',
                  firstErrorField,
                  errors[firstErrorField as string]
                );
              }
            } else {
              // No specific fields to validate for this step, just proceed.
              setCurrentStep((prev) => prev + 1);
            }
          }}
          onGoBack={() => setCurrentStep((prev) => prev - 1)}
          onCancel={() => (cancelUrl ? navigate(cancelUrl) : navigate(-1))}
          isSubmitting={isSubmitting}
          isSavingDraft={isSavingDraft}
          onSaveDraft={handleSaveDraft}
          customActions={customActions}
          submitText={submitText}
          canSubmit={canSubmit}
        />
      </form>
    </FormProvider>
  );
}
